import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { IoService } from '../../../services/io/io.service';
import { AppComponent } from 'src/app/app.component';

// Full-screen vendor availability matrix: target locations (retail +
// warehouse) on the X axis, vendors on the Y axis. A checked cell means the
// vendor delivers to that location. A vendor without servesLocationId serves
// ALL locations — the first uncheck materializes the explicit list. Toggles
// save instantly (optimistic, reverted on error); the per-row "All" button
// clears the restriction back to serves-all.
@Component({
  selector: 'app-vendor-location-grid',
  templateUrl: './vendor-location-grid.component.html',
  styleUrls: ['./vendor-location-grid.component.scss']
})
export class VendorLocationGridComponent implements OnInit {

  vendors: any[] = [];
  locations: any[] = [];
  loading = false;
  loadError: string = null;
  hoverRow = -1;
  hoverCol = -1;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<VendorLocationGridComponent>,
    private ioService: IoService,
  ) { }

  ngOnInit() {
    this.load();
  }

  async load() {
    this.loading = true;
    this.loadError = null;
    try {
      const matrix: any = await this.ioService.post('/location/getVendorLocationMatrix', {});
      this.vendors = matrix.vendors || [];
      this.locations = matrix.locations || [];
    } catch (e) {
      this.loadError = e?.error?.msg || 'Failed to load vendor availability.';
    }
    this.loading = false;
  }

  servesAll(vendor): boolean {
    return !Array.isArray(vendor.servesLocationId);
  }

  serves(vendor, location): boolean {
    if (this.servesAll(vendor)) {
      return true;
    }
    return vendor.servesLocationId.some(id => String(id) === String(location._id));
  }

  // Inactive locations render (after the active ones) and stay toggleable,
  // but the n/m badge counts ACTIVE locations only.
  activeLocationCount(): number {
    return this.locations.filter(location => location.active === true).length;
  }

  servedCount(vendor): number {
    if (this.servesAll(vendor)) {
      return this.activeLocationCount();
    }
    return this.locations.filter(location => location.active === true && this.serves(vendor, location)).length;
  }

  async toggle(vendor, location) {
    if (vendor.saving) {
      return;
    }
    let next: string[];
    if (this.servesAll(vendor)) {
      // First restriction: materialize the full list minus the one turned off.
      next = this.locations.map(l => String(l._id)).filter(id => id !== String(location._id));
    } else {
      const current = vendor.servesLocationId.map(id => String(id));
      next = this.serves(vendor, location)
        ? current.filter(id => id !== String(location._id))
        : [...current, String(location._id)];
    }
    // A list covering every location normalizes back to "serves all" (absent)
    // so the badge reads "All" again.
    if (this.locations.every(l => next.includes(String(l._id)))) {
      await this.saveVendor(vendor, null);
    } else {
      await this.saveVendor(vendor, next);
    }
  }

  // Clicking the vendor name toggles the whole row: everything off when every
  // ACTIVE location is currently served, otherwise everything on (back to
  // serves-all, which covers inactive locations too if they reactivate).
  async toggleVendorAll(vendor) {
    if (vendor.saving) {
      return;
    }
    const allServed = this.servedCount(vendor) === this.activeLocationCount();
    await this.saveVendor(vendor, allServed ? [] : null);
  }

  private async saveVendor(vendor, servesLocationId: string[] | null) {
    const previous = vendor.servesLocationId;
    vendor.saving = true;
    // Optimistic: reflect the toggle instantly, revert if the save fails.
    vendor.servesLocationId = servesLocationId;
    try {
      const updated: any = await this.ioService.post('/location/updateServedLocations', {
        locationId: vendor._id,
        servesLocationId,
      });
      vendor.servesLocationId = Array.isArray(updated?.servesLocationId) ? updated.servesLocationId : null;
    } catch (e) {
      vendor.servesLocationId = previous;
      AppComponent.showSnack(e?.error?.msg || 'Failed to save vendor availability.', 'OK', 4000);
    }
    vendor.saving = false;
  }

  setHover(row: number, col: number) {
    this.hoverRow = row;
    this.hoverCol = col;
  }

  clearHover() {
    this.hoverRow = -1;
    this.hoverCol = -1;
  }

  close() {
    this.dialogRef.close();
  }
}
